import { useEffect, useRef } from 'react'
import { ScrollArea } from '@teach/ui'
import type { ChatItem } from '@/features/ai/lib/chatFromEvents'
import type { StudioJobType } from '@/features/ai/studioTypes'
import type { AnswerGateFailure } from '@/features/ai/useAnswerGate'
import { ChatBubble } from './ChatBubble'
import { GenerationProgress } from './GenerationProgress'

interface Props {
  items: ChatItem[]
  /** Идёт прогон — под лентой рисуется трейлинг-индикатор с прогрессом. */
  isRunning: boolean
  /** Прогон встал на вопросе: индикатор прогресса не рисуется — вопрос уже
   *  в ленте, и крутилка рядом с ним означала бы «от вас ничего не ждут». */
  isAwaitingAuthor: boolean
  /** Ответа джобы ещё нет: индикатор без чисел (см. GenerationProgress). */
  isStatusUnknown: boolean
  progress: { done: number; total: number | null }
  /** Тип прогона, за которым следит индикатор — см. GenerationProgress. */
  jobType: StudioJobType | null
  /** Ответ на карточку вопроса — проброс до `ChatBubble`/`GateCard`. */
  onAnswerGate: (gateId: number, optionKey: string) => void
  isAnsweringGate: boolean
  /** Отказ последнего клика по гейту — проброс до `ChatBubble`/`GateCard`
   *  (Important 1, финальное ревью). */
  gateFailure: AnswerGateFailure | null
}

export const MessageList = ({
  items,
  isRunning,
  isAwaitingAuthor,
  isStatusUnknown,
  progress,
  jobType,
  onAnswerGate,
  isAnsweringGate,
  gateFailure,
}: Props) => {
  const endRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [items, isRunning])

  return (
    // Radix оборачивает контент в `display: table`: длинный неразрывный текст (id элемента)
    // распирал его шире панели, и блоки уходили за границу.
    <ScrollArea className="min-h-0 flex-1 [&_[data-radix-scroll-area-viewport]>div]:!block">
      <div className="flex flex-col space-y-3 p-4">
        {items.map((item) => (
          <div key={item.id}>
            <ChatBubble
              item={item}
              onAnswerGate={onAnswerGate}
              isAnsweringGate={isAnsweringGate}
              gateFailure={gateFailure}
            />
          </div>
        ))}
        {isRunning && !isAwaitingAuthor ? (
          <GenerationProgress
            progress={progress}
            isStatusUnknown={isStatusUnknown}
            jobType={jobType}
          />
        ) : null}
        <div ref={endRef} />
      </div>
    </ScrollArea>
  )
}
